import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface PayoutItem {
  id: string;
  amount: string; // signed sum settled, always > 0
  entry_count: number;
  earned_count: number;
  earned_amount: string;
  reversal_count: number;
  reversal_amount: string; // <= 0
  reference: string;
  note: string | null;
  paid_at: string;
  created_at: string;
  partner: { id: string; name: string; mobile: string };
  created_by: string;
  idempotency_key: string;
  // Phase 4: a masked snapshot of the bank account the money went to, as it was at that
  // moment. null for payouts made without a bank account or before Phase 4.
  destination: {
    bank_name: string | null;
    account_holder_name: string;
    account_number_masked: string;
    account_last4: string;
    ifsc: string;
  } | null;
}

export interface PayoutTotals {
  payout_count: number;
  amount: string;
}

export interface PayoutState {
  items: PayoutItem[];
  totals: PayoutTotals;
  total: number;
  limit: number;
  offset: number;
  loading: boolean;
  error: string | null;
}

const initialState: PayoutState = {
  items: [],
  totals: { payout_count: 0, amount: '0.00' },
  total: 0,
  limit: 25,
  offset: 0,
  loading: false,
  error: null,
};

const payoutSlice = createSlice({
  name: 'payouts',
  initialState,
  reducers: {
    setPayoutLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setPayoutError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setPayoutPage: (
      state,
      action: PayloadAction<{ items: PayoutItem[]; totals: PayoutTotals; total: number; limit: number; offset: number }>
    ) => {
      const { items, totals, total, limit, offset } = action.payload;
      state.items = items;
      state.totals = totals;
      state.total = total;
      state.limit = limit;
      state.offset = offset;
    },
  },
});

export const { setPayoutLoading, setPayoutError, setPayoutPage } = payoutSlice.actions;

export default payoutSlice.reducer;
